import {
  type CollectiveGroupResponse,
  type HeadlineResponsePagedData,
  type PublicPlayerDealResponsePagedData,
  rdbV1CollectiveGroupsDealsDetail,
  rdbV1CollectiveGroupsDetail,
  v1CollectiveGroupsHeadlinesDetail,
} from '@on3/api';
import { proxyApi } from '@on3/ui-lib';
import type { IGenericPageProps } from '@on3/ui-lib/api/schema/custom-contracts';
import { withAuthentication } from '@on3/ui-lib/src/hocs/withAuthentication';
import { withValidUrl } from '@on3/ui-lib/src/hocs/withValidUrl';
import { getFulfilledValue } from '@on3/ui-lib/utils/api';
import { getIdFromParam } from '@on3/ui-lib/utils/url';
import { type AxiosError } from 'axios';
import { DatabaseLayout } from 'components/Layouts/DatabaseLayout';
import { PageWrapper } from 'components/Layouts/PageWrapper';
import { NilCollectiveGroup } from 'components/Nil/Collectives/CollectiveGroup/NilCollectiveGroup';
import type { GetServerSideProps } from 'next';

export interface INilCollectiveGroupPageProps {
  collective?: CollectiveGroupResponse | null;
  articles?: HeadlineResponsePagedData | null;
  nilDeals?: PublicPlayerDealResponsePagedData | null;
}

export interface INilCollectiveGroupPage
  extends INilCollectiveGroupPageProps,
    IGenericPageProps {}

const NilCollectiveGroupPage = (props: INilCollectiveGroupPage) => {
  const { collective } = props;
  const title =
    `${collective?.name} - ${collective?.organization?.fullName} Collective` ||
    'NIL Collective Group';
  const description =
    collective?.missionStatement || `NIL Collective information for ${title}.`;
  const noIndex = !collective?.name;

  return (
    <PageWrapper
      {...props}
      description={description}
      noIndex={noIndex}
      title={title}
    >
      <DatabaseLayout>
        <NilCollectiveGroup {...props} />
      </DatabaseLayout>
    </PageWrapper>
  );
};

export const getServerSideProps: GetServerSideProps = async (ctx) => {
  const nilSiteKey = 368;
  const siteData = await withValidUrl.isValid(ctx, nilSiteKey);
  const userData = await withAuthentication.isAuth(ctx);

  try {
    const { collectiveGroup } = ctx.query as { collectiveGroup: string };
    const collectiveKey = getIdFromParam(collectiveGroup);

    if (!siteData || !collectiveKey) {
      return {
        notFound: true,
      };
    }

    const [collective, articles, nilDeals] = await Promise.allSettled([
      rdbV1CollectiveGroupsDetail(proxyApi, collectiveKey),
      v1CollectiveGroupsHeadlinesDetail(proxyApi, { key: collectiveKey }),
      rdbV1CollectiveGroupsDealsDetail(proxyApi, { key: collectiveKey }),
    ]);

    return {
      props: {
        pageType: 'database',
        siteData,
        userData: userData || {},
        collective: getFulfilledValue(collective),
        articles: getFulfilledValue(articles),
        nilDeals: getFulfilledValue(nilDeals),
      },
    };
  } catch (error) {
    const err: AxiosError<unknown> = error as AxiosError<unknown>;

    console.log(err);

    return { props: {} };
  }
};

export default withValidUrl(NilCollectiveGroupPage);
